import { UnknownAction } from "@reduxjs/toolkit";
import { AcknowledgementPriceFormat, AcknowledgementPriceFormatType, OrderType } from "types/declarations/enums.ts";
import { Dispatch } from "react";
import { arrayGroupBy } from "utils/array";
import { v4 as uuidv4 } from "uuid";
import { models } from "types/api/viewModels.ts";
import { PostOrdersRequestBody } from "types/api/orders/postOrders.ts";
import { newOrderActions } from "features/reducers/newOrder/newOrder";
import {
	EstimatedSubtotalText,
	ListAcknowledgementHelperText,
	NewOrderViewPriceEstimatePromotionalPricingText,
	ReferenceQuoteText
} from "components/NewOrders/constants.ts";
import { NetTotalText, SubtotalText } from "constants/text";

export const GetBackorderCount = (lineItems: models["LineItemViewModel"][] | null | undefined) => {
	const lineItemsOnBackorder = lineItems?.filter(
		(lineItem) => lineItem?.partInfo?.partsQuantityBackordered && lineItem?.partInfo?.partsQuantityBackordered > 0
	);
	const lineItemsCabinetOnBackorder = lineItems?.filter(
		(lineItem) =>
			lineItem.quantityBackordered !== undefined &&
			lineItem.quantityBackordered !== null &&
			lineItem.quantityBackordered > 0
	);
	const hasCabinetBackorder = lineItemsCabinetOnBackorder?.map((e) => e?.quantityBackordered)?.length;
	const hasPartBackorder = lineItemsOnBackorder?.map((e) => e?.partInfo?.partsQuantityBackordered)?.length;
	return Number(
		hasPartBackorder !== undefined && hasCabinetBackorder !== undefined && hasPartBackorder + hasCabinetBackorder
	);
};

export const PartsQuantityBackorderedLength = (lineItems: models["LineItemViewModel"][] | null | undefined) => {
	const lineItemsOnBackorder = lineItems?.filter(
		(lineItem) => lineItem?.partInfo?.partsQuantityBackordered && lineItem?.partInfo?.partsQuantityBackordered > 0
	);
	return lineItemsOnBackorder?.map((e) => e?.partInfo?.partsQuantityBackordered)?.length;
};

export const BackorderedCabinetCount = (lineItems: models["LineItemViewModel"][] | null | undefined) => {
	const lineItemsCabinetOnBackorder = lineItems?.filter(
		(lineItem) =>
			lineItem.quantityBackordered !== undefined &&
			lineItem.quantityBackordered !== null &&
			lineItem.quantityBackordered > 0
	);

	return lineItemsCabinetOnBackorder?.map((e) => e?.quantityBackordered)?.length;
};

export const GetNormalLineItems = (
	lineItems: models["LineItemViewModel"][]
): Map<boolean, models["LineItemViewModel"][]> => {
	return arrayGroupBy(lineItems, (product: { trackingNumber: string | null; truckNumber: string | null }) => {
		return (
			(!product.trackingNumber && !product.truckNumber) ||
			product.trackingNumber === null ||
			product.truckNumber === null
		);
	});
};

export const updateOrderDetails = (
	configurations: models["PendingOrderConfigurationViewModel"][] | null | undefined,
	dispatch: Dispatch<UnknownAction>,
	draftOrder?: models["DraftOrderViewModel"],
	isAddOn?: boolean,
	originalOrderId?: string
) => {
	const tandemOrderId = uuidv4();
	const submittingOrder: PostOrdersRequestBody = {
		tandemOrderId: tandemOrderId,
		jobName: draftOrder?.jobName ?? null,
		poNumber: draftOrder?.poNumber ?? null,
		orderType: isAddOn ? OrderType.ADD_ON : draftOrder?.orderType,
		designerNumber: draftOrder?.designerNumber,
		labelComments: draftOrder?.labelComments ?? null,
		requestedDeliveryDate: draftOrder?.requestedDeliveryDate ?? null,
		originalOrderId: originalOrderId ?? null,
		shipToId: draftOrder?.shipToId ?? null,
		isCustomerPickup: draftOrder?.isCustomerPickup ?? undefined,
		shipToAddress: draftOrder?.shipToAddress ?? {},
		configurations: configurations,
		businessPurpose: draftOrder?.businessPurpose
	};
	const draft: models["DraftOrderViewModel"] = {
		...submittingOrder,
		draftOrderId: draftOrder?.draftOrderId,
		draftName: draftOrder?.draftName,
		designerName: draftOrder?.designerName ?? null,
		pricing: draftOrder?.pricing,
		type: draftOrder?.type
	};

	dispatch(newOrderActions.overwriteDraftOrder(draft));
	dispatch(newOrderActions.overwriteOrderDetails(submittingOrder));
};

export const getOrderLineItems = (orderDetail: models["OrderDetailViewModel"] | null | undefined) => {
	return (
		orderDetail?.configurations
			?.flatMap((config) => config.lineItems)
			?.filter((lineItem) => lineItem !== null && lineItem !== undefined) ?? []
	);
};

export const getModificationsFromLineItem = (lineItem: models["PendingLineItemViewModel"] | null | undefined) => {
	return (
		lineItem?.modifications
			?.map((modification) => ({
				lineItemNumber: modification.lineItemNumber,
				description: modification.description,
				sku: modification.sku,
				quantityOrdered: modification.quantityOrdered
			}))
			?.filter((modification) => modification !== null && modification !== undefined) ?? []
	);
};

export const getOrderAccountInfo = (
	orderDetail: models["OrderDetailViewModel"] | null | undefined,
	accountInfo?: models["CustomerAccountViewModel"][] | null | undefined
) => {
	const [configuration] = orderDetail?.configurations ?? [];
	const matchedAccount = accountInfo?.find((account) => account.accountId === configuration?.accountId);
	const accountNumber = matchedAccount?.accountNumber ?? configuration?.accountNumber;
	const accountName = matchedAccount?.accountName ?? configuration?.accountName;
	return {
		accountId: configuration?.accountId,
		accountName,
		accountNumber,
		billTo: orderDetail?.billTo,
		billToId: configuration?.billToId ?? orderDetail?.billToId,
		accountDisplay: [accountNumber, accountName].filter(Boolean).join(" - ")
	};
};

const validGlobalFilter = (global: models["GlobalsViewModel"] | null | undefined): boolean => {
	if (global === null || global === undefined) {
		return false;
	}

	const hasValidBrand =
		global.brand !== null &&
		global.brand !== undefined &&
		global.brand.code !== null &&
		global.brand.code !== undefined;

	const hasValidProductLine =
		global.productLine !== null &&
		global.productLine !== undefined &&
		global.productLine.code !== null &&
		global.productLine.code !== undefined;
	const hasValidDoorStyle =
		global.style !== null &&
		global.style !== undefined &&
		global.style.code !== null &&
		global.style.code !== undefined;

	return hasValidProductLine || hasValidDoorStyle || hasValidBrand;
};

export const getOrderGlobals = (orderDetail: models["OrderDetailViewModel"] | null | undefined) => {
	return orderDetail?.configurations?.map((config) => config?.globals)?.filter(validGlobalFilter) ?? [];
};

export const getSingleOrderGlobal = (orderDetail: models["OrderDetailViewModel"] | null | undefined) => {
	const [global] = getOrderGlobals(orderDetail);
	return global;
};

// Sorts line items by lineItemNumber
export const lineItemSortCallback = (a: models["LineItemViewModel"], b: models["LineItemViewModel"]) => {
	if (!a?.lineItemNumber || !b?.lineItemNumber) return 0;
	if (a.lineItemNumber < b.lineItemNumber) return -1;
	if (a.lineItemNumber > b.lineItemNumber) return 1;

	return 0;
};

// Sorts Configurations by first line item's lineItemNumber
export const configSortCallback = (
	a: models["OrderConfigurationViewModel"],
	b: models["OrderConfigurationViewModel"]
) => {
	const [firstLineItemA] = a.lineItems ?? [];
	const [firstLineItemB] = b.lineItems ?? [];

	if (!firstLineItemA?.lineItemNumber || !firstLineItemB?.lineItemNumber) return 0;
	if (firstLineItemA.lineItemNumber < firstLineItemB.lineItemNumber) return -1;
	if (firstLineItemA.lineItemNumber > firstLineItemB.lineItemNumber) return 1;

	return 0;
};

// Sorts Orders by first configuration's first line item's lineItemNumber
const orderSortCallback = (a: models["OrderDetailViewModel"], b: models["OrderDetailViewModel"]) => {
	const [firstLineItemA] = a.configurations?.[0]?.lineItems ?? [];
	const [firstLineItemB] = b.configurations?.[0]?.lineItems ?? [];

	if (!firstLineItemA?.lineItemNumber || !firstLineItemB?.lineItemNumber) return 0;
	if (firstLineItemA.lineItemNumber < firstLineItemB.lineItemNumber) return -1;
	if (firstLineItemA.lineItemNumber > firstLineItemB.lineItemNumber) return 1;

	return 0;
};

// Splits an order into a sorted array of orders by shipping information (trackingNumber)
export const groupOrders = (
	orderDetail: models["OrderDetailViewModel"] | null | undefined,
	searchValue: string | null
): models["OrderDetailViewModel"][] => {
	const groups = orderDetail?.configurations?.reduce((accum: Map<string, typeof orderDetail>, current) => {
		current?.lineItems?.forEach((item) => {
			const searchMatch = searchValue ? searchValue === `${item?.sku} ${item.description}` : true;
			const bucketKey = item.trackingNumber ?? "";
			const existingBucket = accum.get(bucketKey);
			if (existingBucket) {
				const existingConfigs = [...(existingBucket.configurations ?? [])];

				if (
					searchMatch &&
					!existingConfigs.some((config) => config.configurationId === current.configurationId)
				) {
					existingConfigs.push({
						...current,
						lineItems: []
					});
				}

				const configurations = existingConfigs.map((config) => {
					if (searchMatch && config.configurationId === current.configurationId) {
						return {
							...config,
							lineItems: config?.lineItems?.concat(item)
						};
					}
					return config;
				});

				accum.set(bucketKey, {
					...existingBucket,
					configurations
				});
			} else {
				accum.set(item.trackingNumber ?? "", {
					...orderDetail,
					configurations: searchMatch ? [{ ...current, lineItems: [item] }] : [],
					shippingDetails: {
						...(bucketKey && { carrierName: item.carrierName, trackingNumber: bucketKey })
					}
				});
			}
		});
		return accum;
	}, new Map<string, typeof orderDetail>());

	const groupedOrderArray = Array.from(groups?.values() ?? []);
	const shipmentOrders = groupedOrderArray.filter((order) => order.shippingDetails?.trackingNumber);
	const nonShipmentOrder = groupedOrderArray.filter((order) => !order.shippingDetails?.trackingNumber);

	shipmentOrders.forEach((order) => {
		order?.configurations?.forEach((config) => {
			config.lineItems?.sort(lineItemSortCallback);
		});
	});
	shipmentOrders.forEach((order) => {
		order?.configurations?.sort(configSortCallback);
	});
	shipmentOrders.sort(orderSortCallback);
	nonShipmentOrder[0]?.configurations?.forEach((config) => {
		config.lineItems?.sort(lineItemSortCallback);
	});
	nonShipmentOrder[0]?.configurations?.sort(configSortCallback);

	return shipmentOrders.concat(nonShipmentOrder);
};

export const checkForCustomQuotePricing = (item: models["LineItemViewModel"], price: number | undefined) => {
	if (item.requiredDimensions?.some((dimension) => dimension.isUnrestrictedValue)) {
		return ReferenceQuoteText;
	} else {
		return price?.toLocaleString("en-US", { style: "currency", currency: "USD" });
	}
};

export enum AcknowledgementType {
	NET_ACKNOWLEDGEMENT = "NET_ACKNOWLEDGEMENT",
	LIST_ACKNOWLEDGEMENT = "LIST_ACKNOWLEDGEMENT",
	NO_ACKNOWLEDGEMENT = "NO_ACKNOWLEDGEMENT"
}

export type AcknowledgementResult = {
	acknowledgementType: AcknowledgementType;
	isAcknowledgementEligible: boolean;
};

/**
 * Determines the appropriate subtitle text based on the order details and acknowledgement type.
 *
 * @param orderDetail - The order detail view model containing order information
 * @param acknowledgementType - The type of acknowledgement for the order
 * @param isOrderDetailsPage
 * @param isCashInAdvance
 * @returns The appropriate subtitle text based on the acknowledgement type and pricing information
 * - Returns SubtotalText for LIST_ACKNOWLEDGEMENT or NO_ACKNOWLEDGEMENT
 * - Returns NetTotalText for NET_ACKNOWLEDGEMENT when netPrice is available
 * - Returns EstimatedSubtotalText as the default case
 */
export const getSubtotalTitle = (
	orderDetail: models["OrderDetailViewModel"],
	acknowledgementType: AcknowledgementType,
	isOrderDetailsPage: boolean = false,
	isCashInAdvance: boolean = false
) => {
	if (
		!isCashInAdvance &&
		(acknowledgementType === AcknowledgementType.LIST_ACKNOWLEDGEMENT ||
			acknowledgementType === AcknowledgementType.NO_ACKNOWLEDGEMENT) &&
		isOrderDetailsPage
	) {
		return SubtotalText;
	}

	if (
		(acknowledgementType === AcknowledgementType.NET_ACKNOWLEDGEMENT &&
			orderDetail.pricing?.netPrice !== undefined &&
			orderDetail.pricing.netPrice !== null) ||
		isCashInAdvance
	) {
		return NetTotalText;
	}

	return EstimatedSubtotalText;
};

/**
 * Determines whether promotional pricing text should be displayed.
 *
 * @param acknowledgementType - The type of acknowledgement for the order
 * @param subtotalTitle - The title used for the subtotal section
 * @returns True if the acknowledgement type is not NET_ACKNOWLEDGEMENT and the subtotal title matches EstimatedSubtotalText
 */
export const shouldShowPromotionalPricingText = (
	acknowledgementType: AcknowledgementType,
	subtotalTitle: string
): boolean => {
	return (
		acknowledgementType === AcknowledgementType.NO_ACKNOWLEDGEMENT &&
		(subtotalTitle === EstimatedSubtotalText || subtotalTitle === SubtotalText)
	);
};

export const shouldShowListAcknowledgementHelperText = (
	acknowledgementType: AcknowledgementType,
	subtotalTitle: string
): boolean => {
	return acknowledgementType === AcknowledgementType.LIST_ACKNOWLEDGEMENT && subtotalTitle === SubtotalText;
};

/**
 * Returns promotional pricing text if it should be shown, otherwise returns false.
 *
 * @param acknowledgementType - The type of acknowledgement for the order
 * @param subtotalTitle - The title of the subtotal
 * @returns The promotional pricing text or false if it shouldn't be shown
 */
export const getPromotionalPricingText = (
	acknowledgementType: AcknowledgementType,
	subtotalTitle: string
): string | false => {
	return shouldShowPromotionalPricingText(acknowledgementType, subtotalTitle)
		? NewOrderViewPriceEstimatePromotionalPricingText
		: false;
};

export const getListAcknowledgementHelperText = (
	acknowledgementType: AcknowledgementType,
	subtotalTitle: string
): string | false => {
	return shouldShowListAcknowledgementHelperText(acknowledgementType, subtotalTitle)
		? ListAcknowledgementHelperText
		: false;
};

/**
 * Determines the acknowledgement eligibility and type for an order detail.
 *
 * @param orderDetail - The order detail to check for acknowledgement eligibility
 * @param configurationsData - The configuration data containing feature flags
 * @param ackPermission
 * @returns An object containing the acknowledgement type and eligibility status
 *          - acknowledgementType: The type of acknowledgement (NO_ACKNOWLEDGEMENT, NET_ACKNOWLEDGEMENT, or LIST_ACKNOWLEDGEMENT)
 *          - isAcknowledgementEligible: Boolean indicating if the order is eligible for acknowledgement
 */
export const checkAcknowledgementEligibility = (
	orderDetail: models["OrderDetailViewModel"] | null | undefined,
	configurationsData: models["ConfigurationViewModel"] | null | undefined,
	ackPermission?: AcknowledgementPriceFormatType | null
): AcknowledgementResult => {
	// Check if acknowledgements are enabled and order is eligible
	const isAcknowledgementAllowed = Boolean(
		configurationsData?.featureFlags?.acknowledgementsEnabled && orderDetail?.isAcknowledgementEligible
	);

	if (!isAcknowledgementAllowed) {
		return {
			acknowledgementType: AcknowledgementType.NO_ACKNOWLEDGEMENT,
			isAcknowledgementEligible: false
		};
	}

	return {
		acknowledgementType:
			ackPermission === AcknowledgementPriceFormat.LIST
				? AcknowledgementType.LIST_ACKNOWLEDGEMENT
				: AcknowledgementType.NET_ACKNOWLEDGEMENT,
		isAcknowledgementEligible: true
	};
};

export const isFinalPricePromotionApplied = (
	item: models["LineItemViewModel"] | null | undefined,
	isNet?: boolean
): boolean => {
	if (!item || !isNet) {
		return false;
	}

	// Check for final price at both line item level and if there's a promotion applied
	const hasFinalPrice = item?.pricing?.finalNetPrice !== undefined && item?.pricing?.finalNetPrice !== null;
	const hasPromotion = (item.pricing?.promotions?.length ?? 0) > 0;

	return hasPromotion && hasFinalPrice;
};
